import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

const CopyableText = ({ text, copyValue, className = "", children }) => {
    const [tooltipText, setTooltipText] = useState('Click to copy');
    const [showTooltip, setShowTooltip] = useState(false);
    const [coords, setCoords] = useState({ top: 0, left: 0 });
    const triggerRef = useRef(null);

    const updateCoords = () => {
        if (triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            setCoords({
                top: rect.top - 8, // Just above the element
                left: rect.left + rect.width / 2
            });
        }
    };

    const handleCopy = (e) => {
        e.stopPropagation();
        e.preventDefault(); // Prevent accidental selection
        const valueToCopy = copyValue || text;

        if (!valueToCopy) return;

        navigator.clipboard.writeText(valueToCopy).then(() => {
            setTooltipText('Copied to clipboard');
            updateCoords(); // Update coords in case of scroll/movement
            setTimeout(() => {
                if (triggerRef.current) { // Check if still mounted
                    setTooltipText('Click to copy');
                }
            }, 2000);
        }).catch(err => {
            console.error('Failed to copy text: ', err);
            setTooltipText('Failed to copy');
        });
    };

    const handleMouseEnter = () => {
        updateCoords();
        setShowTooltip(true);
    };

    const handleMouseLeave = () => {
        setShowTooltip(false);
        setTimeout(() => setTooltipText('Click to copy'), 200);
    };

    // Close tooltip on scroll to prevent it from detaching
    useEffect(() => {
        if (showTooltip) {
            window.addEventListener('scroll', updateCoords, true);
            window.addEventListener('resize', updateCoords);
        }
        return () => {
            window.removeEventListener('scroll', updateCoords, true);
            window.removeEventListener('resize', updateCoords);
        };
    }, [showTooltip]);

    return (
        <>
            <span
                ref={triggerRef}
                className={`group relative inline-block cursor-pointer hover:text-indigo-600 border border-transparent hover:border-indigo-300 rounded px-1 transition-all duration-200 ${className}`}
                onClick={handleCopy}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                {children || text}
            </span>

            {showTooltip && createPortal(
                <span
                    className="fixed pointer-events-none bg-gray-800 text-white text-[10px] rounded py-1 px-2 whitespace-nowrap z-[9999] shadow-lg transition-opacity duration-200"
                    style={{
                        top: coords.top,
                        left: coords.left,
                        transform: 'translate(-50%, -100%)',
                        opacity: showTooltip ? 1 : 0
                    }}
                >
                    {tooltipText}
                    {/* Triangle arrow */}
                    <span className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-[1px] border-4 border-transparent border-t-gray-800"></span>
                </span>,
                document.body
            )}
        </>
    );
};

export default CopyableText;
